import type { Theme } from '@/types'

import { useCallback, useEffect, useState } from 'react'

export const THEME_STORAGE_KEY = 'fz-theme'

/**
 * Document-level theme flag. The Frieze section mirrors `data-theme` on <html>
 * through a MutationObserver, so this only ever writes the attribute.
 *
 * Server-safe: the state starts on a static default and the browser is read
 * once, after mount.
 */
export const useTheme = () => {
    const [theme, setThemeState] = useState<Theme>('dark')

    useEffect(() => {
        const current = document.documentElement.getAttribute('data-theme')

        if (current === 'light' || current === 'dark') setThemeState(current)
    }, [])

    const setTheme = useCallback((next: Theme) => {
        setThemeState(next)
        document.documentElement.setAttribute('data-theme', next)

        try {
            window.localStorage.setItem(THEME_STORAGE_KEY, next)
        } catch {
            /* storage unavailable — the flag still applies for this session */
        }
    }, [])

    const toggleTheme = useCallback(() => {
        setTheme(theme === 'dark' ? 'light' : 'dark')
    }, [theme, setTheme])

    return { theme, setTheme, toggleTheme }
}
